/**
 * useOneShotFetch Hook
 *
 * Generic one-time fetch hook for immutable Firestore collections.
 * Handles loading, error, and cancellation. The SDK's
 * persistentLocalCache provides offline support automatically.
 */

import { useState, useEffect } from "react";

interface UseOneShotFetchOptions<T> {
  /** Optional comparator to sort results after fetch */
  sort?: (a: T, b: T) => number;
}

interface UseOneShotFetchResult<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
}

export function useOneShotFetch<T>(
  fetchFn: () => Promise<T[]>,
  options?: UseOneShotFetchOptions<T>
): UseOneShotFetchResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetchFn()
      .then((result) => {
        if (!cancelled) {
          setData(options?.sort ? [...result].sort(options.sort) : result);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // fetchFn and sort are expected to be stable references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, error };
}
